import { useState, useEffect } from 'react';
import {
    Grid,
    Card,
    Typography,
    Stack,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { fNumber, fCurrency } from '../../../utils/formatNumber';
import { RecallsService } from '../../../api/recalls.service';

const recallsService = new RecallsService();

export default function RecallAnalytics() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const response = await recallsService.getAnalytics();
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch recall analytics', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    // 1. Process Funnel Data
    const funnelStages = {
        'Stage 1 (SMS)': data.funnel.filter((f: any) => f.stage === 1).reduce((acc: number, curr: any) => acc + Number(curr.count), 0),
        'Stage 2 (Email)': data.funnel.filter((f: any) => f.stage === 2).reduce((acc: number, curr: any) => acc + Number(curr.count), 0),
        'Stage 3 (Phone)': data.funnel.filter((f: any) => f.stage === 3).reduce((acc: number, curr: any) => acc + Number(curr.count), 0),
    };

    const funnelChartOptions: any = {
        chart: { type: 'bar', height: 350 },
        plotOptions: {
            bar: {
                borderRadius: 4,
                horizontal: true,
                barHeight: '80%',
                distributed: true,
            },
        },
        colors: ['#008FFB', '#00E396', '#FEB019'],
        dataLabels: {
            enabled: true,
            textAnchor: 'start',
            style: { colors: ['#fff'] },
            formatter: function (val: any, opt: any) {
                return opt.w.globals.labels[opt.dataPointIndex] + ": " + val;
            },
            offsetX: 0,
        },
        xaxis: {
            categories: Object.keys(funnelStages),
        },
        title: {
            text: 'Patient Distribution by Recall Stage',
            align: 'center',
        }
    };

    const funnelChartSeries = [{
        name: 'Patients',
        data: Object.values(funnelStages),
    }];

    return (
        <Grid container spacing={3} sx={{ p: 2 }}>
            {/* 1. Recall Funnel */}
            <Grid item xs={12} md={6}>
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3 }}>Recall Funnel</Typography>
                    <ReactApexChart options={funnelChartOptions} series={funnelChartSeries} type="bar" height={300} />
                </Card>
            </Grid>

            {/* 2. Credit Usage Summary */}
            <Grid item xs={12} md={6}>
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3 }}>Messaging Economics</Typography>
                    <Stack spacing={3}>
                        {data.creditUsage.map((u: any) => (
                            <Box key={`${u.channel}-${u.messageType}`}>
                                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                    {u.channel} - {u.messageType}
                                </Typography>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="h4">{fNumber(u.totalMessages)} <small style={{ fontSize: '0.5em' }}>msgs</small></Typography>
                                    <Typography variant="h4" color="primary">{fCurrency(u.totalCredits || 0)} <small style={{ fontSize: '0.5em' }}>spent</small></Typography>
                                </Stack>
                                <Divider sx={{ mt: 1 }} />
                            </Box>
                        ))}
                        {data.creditUsage.length === 0 && (
                            <Typography variant="body2" sx={{ textAlign: 'center', py: 5 }}>No messaging data recorded yet.</Typography>
                        )}
                    </Stack>
                </Card>
            </Grid>

            {/* 3. Campaign Conversions */}
            <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Campaign Performance Tracking</Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Campaign ID</TableCell>
                                    <TableCell align="center">Patients Targeted</TableCell>
                                    <TableCell align="center">Bookings (Conversions)</TableCell>
                                    <TableCell align="right">Spend Uplift</TableCell>
                                    <TableCell align="right">ROAS (Est.)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.campaigns.map((c: any) => (
                                    <TableRow key={c.campaignId}>
                                        <TableCell>{c.campaignId.substring(0, 8)}...</TableCell>
                                        <TableCell align="center">{c.totalSent}</TableCell>
                                        <TableCell align="center">{c.conversions}</TableCell>
                                        <TableCell align="right">{fCurrency(c.spendUplift || 0)}</TableCell>
                                        <TableCell align="right">
                                            {c.totalSent > 0 ? `${((Number(c.spendUplift || 0) / (c.totalSent * 0.1)) * 100).toFixed(0)}%` : '0%'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {data.campaigns.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">No active campaign tracking available.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Grid>

            {/* 4. Staff Productivity */}
            <Grid item xs={12} md={6}>
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Manual Recall Audit (Phone)</Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Staff Member</TableCell>
                                    <TableCell align="center">Total Calls</TableCell>
                                    <TableCell align="center">Success %</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.staffProductivity.map((s: any) => (
                                    <TableRow key={s.staffName}>
                                        <TableCell>{s.staffName}</TableCell>
                                        <TableCell align="center">{s.totalCalls}</TableCell>
                                        <TableCell align="center">
                                            {s.totalCalls > 0 ? `${((s.successfulCalls / s.totalCalls) * 100).toFixed(0)}%` : '0%'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {data.staffProductivity.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">No phone logs recorded.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Grid>

            {/* 5. Activity Feed */}
            <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Recent Activity Feed</Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Patient</TableCell>
                                    <TableCell>Channel</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Cost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.recentActivities.map((a: any) => (
                                    <TableRow key={a.id}>
                                        <TableCell>{new Date(a.timestamp).toLocaleString()}</TableCell>
                                        <TableCell>{a.patientName}</TableCell>
                                        <TableCell>{a.channel}</TableCell>
                                        <TableCell>{a.type}</TableCell>
                                        <TableCell>{a.status}</TableCell>
                                        <TableCell align="right">{fCurrency(a.cost || 0)}</TableCell>
                                    </TableRow>
                                ))}
                                {data.recentActivities.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No recent activity detected.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Grid>
        </Grid>
    );
}
